/**
 * The firm's own map of its city: a tree of areas, with ranks inside them.
 *
 * The tree is the point. A flat list of zones cannot express "the rank inside the district
 * inside the city", which is how every firm describes their patch, and it is what the offer
 * ladder walks outward along. Depth is capped at three, and the parent list here refuses to
 * offer a zone that is already as deep as it goes.
 *
 * Areas and ranks are two tables of the same tree rather than one, because they are read
 * for different reasons: the areas are the shape of the city and are read as a hierarchy,
 * the ranks are places drivers queue and are read as a list of where. Both edit the same
 * record through the same dialog, and the kind is a field on it, so a zone that turns out
 * to be a rank moves between the tables by being saved.
 *
 * Zones are edited in place rather than versioned, unlike tariffs. A boundary is a statement
 * about where a place is; correcting one is fixing a mistake, not changing a price, and the
 * fares it already produced are stored on their orders and never recomputed.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveMapView, type MapHome } from '@taxi/api-client';

import { mapApi, type MapConfiguration } from '../../map/api';
import {
  Banner,
  Button,
  ConfirmDialog,
  cx,
  Dialog,
  EmptyState,
  Field,
  FieldErrors,
  Flagged,
  Icon,
  Input,
  PageHeader,
  Pill,
  Section,
  Select,
  Switch,
  Table,
  Td,
  Th,
  Tr,
  type FieldProblem,
} from '../../ui';
import {
  maxZoneDepth,
  settingsApi,
  surchargeTriggers,
  zoneKinds,
  zoneShapes,
  type SurchargeTrigger,
  type Zone,
  type ZoneDraft,
  type ZoneKind,
  type ZoneShape,
  type ZoneVertex,
} from '../api';
import { refusal } from '../errors';
import { checkForm, named as withLabel } from '../check';
import { huesFor, openRing, outlineOf, RADIUS_MIN, ringOf } from './geometry';
import { HuePicker } from './HuePicker';
import { RadiusControl } from './RadiusControl';

/**
 * MapLibre is most of a megabyte, and this is the one screen in the console that draws a
 * boundary. Loaded when somebody opens the form, so the dispatch screens never pay for it.
 */
const ZoneMap = lazy(async () => ({ default: (await import('./ZoneMap')).ZoneMap }));
const ZonesOverview = lazy(async () => ({
  default: (await import('./ZonesOverview')).ZonesOverview,
}));

const FORM = 'zone-form';

/** Where the editor opens for a firm that has said nothing at all: Europe, obviously unset. */
const NOWHERE_IN_PARTICULAR = { longitude: 25, latitude: 42, zoom: 5 };

/** A city, for a firm whose view is a point without a zoom of its own. */
const CITY_ZOOM = 11;

/** How long a confirmation stays up before it stops being news. */
const CONFIRMATION_MS = 4000;

/**
 * The page's two-column layout, switched on at 1440px: the sidebar, a 900px settings
 * column and at least 480px of map is what that width holds. Under it the map goes below
 * the tables. The map's height on the wide layout is the window less the bar, the card's
 * header, the legend and the page's padding, so the whole city fits without scrolling.
 */
const WIDE_LAYOUT = {
  grid: 'min-[1440px]:grid-cols-[minmax(0,900px)_minmax(480px,1fr)]',
  aside: 'min-[1440px]:sticky min-[1440px]:top-0',
  mapHeight: 'min-[1440px]:h-[calc(100dvh-270px)] min-[1440px]:min-h-[440px]',
} as const;

function blank(kind: ZoneKind): ZoneDraft {
  return {
    code: '',
    displayName: '',
    parentId: null,
    kind,
    shape: 'Circle',
    centreLongitude: null,
    centreLatitude: null,
    radiusMetres: 500,
    boundary: null,
    surchargeAmount: 0,
    surchargePercent: 0,
    surchargeAppliesTo: 'Either',
    isActive: true,
    hue: null,
  };
}

export function ZonesPage() {
  const { t } = useTranslation();

  const [zones, setZones] = useState<Zone[] | null>(null);
  const [configuration, setConfiguration] = useState<MapConfiguration | null>(null);
  const [home, setHome] = useState<MapHome | null>(null);
  const [draft, setDraft] = useState<(ZoneDraft & { id?: string }) | null>(null);

  /**
   * The boundary as it was before each change, newest last, for Undo.
   *
   * A stack rather than "remove the last corner", because a corner is no longer always
   * added at the end: it goes into whichever side was clicked, and it can be dragged. What
   * a person wants back after either is the shape before it, not the last item in a list.
   */
  const [history, setHistory] = useState<ZoneVertex[][]>([]);
  const [removing, setRemoving] = useState<Zone | null>(null);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setZones(await settingsApi.zones());
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();

    void (async () => {
      try {
        // The firm's home view rides along, and its failure is not the map's: a firm
        // with nothing set still gets a map, opened on the deployment's centre.
        const [style, view] = await Promise.all([
          mapApi.configuration(),
          mapApi.home().catch(() => null),
        ]);

        setHome(view);
        setConfiguration(style);
      } catch {
        // The map is how a boundary is drawn, not how a zone is saved. A firm whose tile
        // provider is down can still edit a circle by typing three numbers.
        setConfiguration(null);
      }
    })();
  }, [load]);

  useEffect(() => {
    if (!saved) return;

    const timer = setTimeout(() => setSaved(false), CONFIRMATION_MS);

    return () => clearTimeout(timer);
  }, [saved]);

  async function save() {
    const invalid = checkForm(document.getElementById(FORM), t);

    if (invalid !== null) {
      setError(invalid);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (draft === null) return;

      const { id, ...body } = draft;

      await (id === undefined
        ? settingsApi.createZone(body)
        : settingsApi.updateZone(id, body));

      // Closed only once the server has taken it. A dialog that closes on submit throws
      // away everything typed into it the moment the code turns out to be a duplicate.
      setDraft(null);
      setSaved(true);
      await load();
    } catch (failure) {
      setError(withLabel(refusal(failure, t, 'settings.couldNotSave'), document.getElementById(FORM), t));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    setError(null);
    setRemoving(null);

    try {
      await settingsApi.deleteZone(id);
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    }
  }

  /** Opens the editor on a draft, with nothing to undo yet. */
  function open(next: ZoneDraft & { id?: string }) {
    setHistory([]);
    setDraft(next);
  }

  function edit(zone: Zone) {
    const { id, depth: _depth, boundary, ...rest } = zone;

    // The API hands a polygon back closed; the editor counts corners, so it is opened here.
    open({ id, ...rest, boundary: boundary === null ? null : openRing(boundary) });
  }

  function change<K extends keyof ZoneDraft>(key: K, value: ZoneDraft[K]) {
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  }

  /** Remembers the boundary as it is now, so the next change can be undone. */
  function rememberBoundary() {
    if (draft === null || draft.shape !== 'Polygon') return;

    const before = draft.boundary ?? [];

    setHistory((stack) => [...stack, before]);
  }

  /** Changes the boundary, remembering what it was. */
  function changeBoundary(next: ZoneVertex[]) {
    rememberBoundary();
    change('boundary', next);
  }

  function undoBoundary() {
    const previous = history.at(-1);

    if (previous === undefined) return;

    setHistory((stack) => stack.slice(0, -1));
    change('boundary', previous);
  }

  // A zone already at the deepest level cannot be anybody's parent, and neither can the
  // zone being edited — a zone inside itself is a tree that never terminates, and the
  // ladder walks this tree on every order. Nor can a rank: the ladder takes the rank as
  // the deepest thing at a point and everything above it as an area, so a zone inside a
  // rank would be walked as if it were a district.
  const parents = (zones ?? []).filter(
    (zone) => zone.kind === 'Area' && zone.depth < maxZoneDepth && zone.id !== draft?.id,
  );

  // What both maps draw: every zone in its own colour, and — for the editor — every zone
  // but the one open. The colours are chosen over the whole list, so the editor's
  // neighbours wear the same ones the overview does.
  const outlines = useMemo(() => {
    const hues = huesFor((zones ?? []).map((zone) => ({ ...zone, ring: ringOf(zone) })));

    return (zones ?? []).map((zone) => outlineOf(zone, hues.get(zone.id) ?? 0));
  }, [zones]);
  const others = useMemo(
    () => outlines.filter((zone) => zone.id !== draft?.id),
    [outlines, draft?.id],
  );

  const mapView = resolveMapView(home, configuration, {
    zoom: CITY_ZOOM,
    nowhere: NOWHERE_IN_PARTICULAR,
  });

  const areas = (zones ?? []).filter((zone) => zone.kind !== 'Rank');
  const ranks = (zones ?? []).filter((zone) => zone.kind === 'Rank');

  const named = (id: string | null) =>
    (zones ?? []).find((zone) => zone.id === id)?.displayName ?? '—';

  function describeShape(zone: Zone) {
    return zone.shape === 'Circle'
      ? t('zones.circleOf', { radius: zone.radiusMetres ?? 0 })
      : t('zones.polygonOf', { corners: (zone.boundary?.length ?? 1) - 1 });
  }

  function describeCharge(zone: Zone) {
    return zone.surchargeAmount > 0 || zone.surchargePercent > 0
      ? t('zones.charges', { percent: zone.surchargePercent, amount: zone.surchargeAmount })
      : '—';
  }

  // `zoneName` rather than `name`, which is a global — one that is a string, so shadowing
  // it with a function is the sort of thing that compiles and confuses.
  function zoneName(zone: Zone) {
    return (
      <>
        {zone.displayName}
        {!zone.isActive && (
          <Pill soft className="ml-2">
            {t('zones.off')}
          </Pill>
        )}
      </>
    );
  }

  function rowActions(zone: Zone) {
    return (
      <span className="flex justify-end gap-1.5">
        <Button size="sm" onClick={() => edit(zone)}>
          {t('surcharges.edit')}
        </Button>
        <Button size="sm" variant="danger" onClick={() => setRemoving(zone)}>
          {t('surcharges.remove')}
        </Button>
      </span>
    );
  }

  // The drawing buttons, in the form and — while the map covers the page and the form is
  // out of reach — over the map. One element, rendered in whichever place is visible.
  const drawingTools =
    draft !== null && draft.shape === 'Polygon' ? (
      <>
        <Button size="sm" disabled={history.length === 0} onClick={undoBoundary}>
          {t('zones.undo')}
        </Button>
        <Button
          size="sm"
          disabled={(draft.boundary?.length ?? 0) === 0}
          onClick={() => changeBoundary([])}
        >
          {t('zones.clearCorners')}
        </Button>
      </>
    ) : undefined;

  const overview = (
    <Section title={t('zones.overview')} description={t('zones.overviewAbout')}>
      {zones === null || configuration === null ? (
        <EmptyState>{t('app.loading')}</EmptyState>
      ) : (
        <div className="p-3">
          <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
            <ZonesOverview
              configuration={configuration}
              view={mapView}
              zones={outlines}
              className={cx('h-110', WIDE_LAYOUT.mapHeight)}
              onSelect={(id) => {
                const zone = zones.find((candidate) => candidate.id === id);

                if (zone !== undefined) edit(zone);
              }}
            />
          </Suspense>
        </div>
      )}
    </Section>
  );

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      {/* Two columns on a wide screen — the tables in the settings column on the left, the
          map in whatever is left, which on a dispatch workstation is most of the screen —
          and one column on a narrow one, where the map goes below the tables. The map is
          sticky, so scrolling the tables leaves the city in view. */}
      <div className={cx('grid grid-cols-1 gap-4 items-start', WIDE_LAYOUT.grid)}>
        <div className="flex flex-col gap-4 min-w-0">
          <PageHeader title={t('nav.settings.zones')} description={t('nav.settingsAbout.zones')} />

          {/* Only while the dialog is shut. The dialog repeats it inside itself, because a modal
              covers this strip and makes it inert — and the form stays open on a refusal. */}
          {error !== null && draft === null && <Banner tone="danger">{error.message}</Banner>}
          {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}

          <Section
            title={t('zones.tree')}
            description={t('zones.treeHelp')}
            actions={
              <Button size="sm" onClick={() => open(blank('Area'))}>
                <Icon name="plus" small />
                {t('zones.add')}
              </Button>
            }
          >
            {zones === null ? (
              <EmptyState>{t('app.loading')}</EmptyState>
            ) : areas.length === 0 ? (
              <EmptyState>{t('zones.none')}</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('zones.columnZone')}</Th>
                    <Th>{t('zones.code')}</Th>
                    <Th>{t('zones.shapeLabel')}</Th>
                    <Th numeric>{t('zones.columnSurcharge')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {areas.map((zone) => (
                    <Tr key={zone.id}>
                      {/* The nesting is the setting. Indented rather than given a column of its
                          own, because a tree three deep is read down the left edge. */}
                      <Td>
                        <span
                          className="inline-flex items-center"
                          style={{ paddingLeft: `${(zone.depth - 1) * 20}px` }}
                        >
                          {zoneName(zone)}
                        </span>
                      </Td>
                      <Td>{zone.code}</Td>
                      <Td>{describeShape(zone)}</Td>
                      <Td numeric>{describeCharge(zone)}</Td>
                      <Td>{rowActions(zone)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>

          <Section
            title={t('zones.ranks')}
            description={t('zones.ranksHelp')}
            actions={
              <Button size="sm" onClick={() => open(blank('Rank'))}>
                <Icon name="plus" small />
                {t('zones.addRank')}
              </Button>
            }
          >
            {zones === null ? (
              <EmptyState>{t('app.loading')}</EmptyState>
            ) : ranks.length === 0 ? (
              <EmptyState>{t('zones.noRanks')}</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('zones.rank')}</Th>
                    <Th>{t('zones.code')}</Th>
                    <Th>{t('zones.parent')}</Th>
                    <Th>{t('zones.shapeLabel')}</Th>
                    <Th numeric>{t('zones.columnSurcharge')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {ranks.map((zone) => (
                    <Tr key={zone.id}>
                      <Td>{zoneName(zone)}</Td>
                      <Td>{zone.code}</Td>
                      <Td>{named(zone.parentId)}</Td>
                      <Td>{describeShape(zone)}</Td>
                      <Td numeric>{describeCharge(zone)}</Td>
                      <Td>{rowActions(zone)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>

        </div>

        <div className={cx('min-w-0', WIDE_LAYOUT.aside)}>{overview}</div>
        </div>

      {/* Asked, because a mis-click on this row removes the city boundary, and there is no
          undo: the ladder and the fares lose the zone the moment the server takes it. */}
      <ConfirmDialog
        open={removing !== null}
        title={t('zones.confirmRemoveTitle')}
        confirm={t('surcharges.remove')}
        cancel={t('surcharges.cancelEdit')}
        onConfirm={() => {
          if (removing !== null) void remove(removing.id);
        }}
        onClose={() => setRemoving(null)}
      >
        {removing !== null && t('zones.confirmRemove', { name: removing.displayName })}
      </ConfirmDialog>

      <Dialog
        open={draft !== null}
        wide
        onClose={() => setDraft(null)}
        title={
          draft === null
            ? ''
            : draft.id !== undefined
              ? t('zones.editing', { name: draft.displayName })
              : draft.kind === 'Rank'
                ? t('zones.addRank')
                : t('zones.add')
        }
        actions={
          <>
            <Button variant="quiet" onClick={() => setDraft(null)}>
              {t('surcharges.cancelEdit')}
            </Button>
            <Button variant="primary" type="submit" form={FORM} disabled={saving}>
              {saving ? t('settings.saving') : t('zones.save')}
            </Button>
          </>
        }
      >
        {draft !== null && (
          <form
            id={FORM}
            noValidate
            className="flex flex-col gap-3.5"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            {error !== null && <Banner tone="danger">{error.message}</Banner>}

            <Field id="code" label={t('zones.code')} help={t('zones.codeHelp')}>
              <Input
                value={draft.code}
                onChange={(e) => change('code', e.target.value)}
                required
              />
            </Field>

            <Field id="displayName" label={t('zones.name')} help={t('zones.nameHelp')}>
              <Input
                value={draft.displayName}
                onChange={(e) => change('displayName', e.target.value)}
                required
              />
            </Field>

            <Field id="hue" label={t('zones.hue')} help={t('zones.hueHelp')}>
              <HuePicker
                value={draft.hue}
                automatic={
                  draft.hue === null
                    ? (outlines.find((zone) => zone.id === draft.id)?.hue ?? null)
                    : null
                }
                onChange={(hue) => change('hue', hue)}
              />
            </Field>

            <Field id="parentId" label={t('zones.parent')} help={t('zones.parentHelp')}>
              <Select
                value={draft.parentId ?? ''}
                onChange={(e) => change('parentId', e.target.value === '' ? null : e.target.value)}
              >
                <option value="">{t('zones.noParent')}</option>
                {parents.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {'— '.repeat(zone.depth - 1)}
                    {zone.displayName}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="kind" label={t('zones.kindLabel')} help={t('zones.kindLabelHelp')}>
              <Select
                value={draft.kind}
                onChange={(e) => change('kind', e.target.value as ZoneKind)}
              >
                {zoneKinds.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`zones.kind.${kind}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="shape" label={t('zones.shapeLabel')} help={t('zones.shapeLabelHelp')}>
              <Select
                value={draft.shape}
                onChange={(e) => change('shape', e.target.value as ZoneShape)}
              >
                {zoneShapes.map((shape) => (
                  <option key={shape} value={shape}>
                    {t(`zones.shape.${shape}`)}
                  </option>
                ))}
              </Select>
            </Field>

            {draft.shape === 'Circle' ? (
              <Field id="radiusMetres" label={t('zones.radius')} help={t('zones.radiusHelp')}>
                <RadiusControl
                  value={draft.radiusMetres ?? 500}
                  onChange={(metres) => change('radiusMetres', metres)}
                />
              </Field>
            ) : (
              <div className="flex gap-2">{drawingTools}</div>
            )}

            {/* The shape is drawn, not typed, so a refusal about a missing centre or boundary
              lands on the map. */}
            <Flagged id="boundary" also={['centre']}>
            <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
              <ZoneMap
                configuration={configuration}
                view={mapView}
                shape={draft.shape}
                centre={
                  draft.centreLongitude === null || draft.centreLatitude === null
                    ? null
                    : { longitude: draft.centreLongitude, latitude: draft.centreLatitude }
                }
                // A box being retyped holds NaN for a moment; the circle keeps its smallest
                // size meanwhile rather than vanishing.
                radiusMetres={Number.isFinite(draft.radiusMetres) ? (draft.radiusMetres ?? 500) : RADIUS_MIN}
                boundary={draft.boundary ?? []}
                others={others}
                tools={
                  draft.shape === 'Circle' ? (
                    <RadiusControl
                      floating
                      value={draft.radiusMetres ?? 500}
                      onChange={(metres) => change('radiusMetres', metres)}
                    />
                  ) : (
                    drawingTools
                  )
                }
                onCentre={(point: ZoneVertex) =>
                  setDraft((current) =>
                    current === null
                      ? current
                      : {
                          ...current,
                          centreLongitude: point.longitude,
                          centreLatitude: point.latitude,
                        },
                  )
                }
                onVertex={(point: ZoneVertex, at: number) =>
                  changeBoundary((draft.boundary ?? []).toSpliced(at, 0, point))
                }
                onBeginDrag={rememberBoundary}
                onMoveVertex={(index: number, point: ZoneVertex) =>
                  setDraft((current) =>
                    current === null
                      ? current
                      : {
                          ...current,
                          boundary: (current.boundary ?? []).map((corner, at) =>
                            at === index ? point : corner,
                          ),
                        },
                  )
                }
              />
            </Suspense>
            </Flagged>

            <Field id="surchargePercent" label={t('zones.surchargePercent')} help={t('zones.surchargePercentHelp')}>
              <Input
                type="number"
                min={0}
                max={500}
                step={0.5}
                value={draft.surchargePercent}
                onChange={(e) => change('surchargePercent', e.target.valueAsNumber)}
              />
            </Field>

            <Field id="surchargeAmount" label={t('zones.surchargeAmount')} help={t('zones.surchargeAmountHelp')}>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={draft.surchargeAmount}
                onChange={(e) => change('surchargeAmount', e.target.valueAsNumber)}
              />
            </Field>

            <Field id="surchargeAppliesTo" label={t('zones.appliesTo')} help={t('zones.appliesToHelp')}>
              <Select
                value={draft.surchargeAppliesTo}
                onChange={(e) =>
                  change('surchargeAppliesTo', e.target.value as SurchargeTrigger)
                }
              >
                {surchargeTriggers.map((trigger) => (
                  <option key={trigger} value={trigger}>
                    {t(`zones.trigger.${trigger}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="isActive" label={t('zones.active')} help={t('zones.activeHelp')}>
              <Switch
                checked={draft.isActive}
                label={t('zones.active')}
                onChange={(next) => change('isActive', next)}
              />
            </Field>
          </form>
        )}
      </Dialog>
    </FieldErrors>
  );
}
